/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ICellEditor } from "@ag-grid-community/core";
import { allOptionsValueLiteral, ConciseSelector, renderMultiSelectLabel } from "@iventis/components";

import { Theme } from "@emotion/react";
import { styled } from "@iventis/styles";
import { Popover, useTheme } from "@mui/material";
import { ListItemCellEditorProps, ListItem, RowNetworkStatus } from "../types/data-table.types";
import { useLazyLoadOptions } from "../lib/hooks";
import { cellEditorBase } from "../styles/data-table.styles";

/**
 * Used as cellEditor for data table column of type "multi_select"
 */
export const MultiSelectCellEditorComponent = forwardRef<ICellEditor, ListItemCellEditorProps>(
    ({ value: currentlySelectedValues, componentParams, filterOptions, eGridCell, api, column, node, context, required }, ref) => {
        const theme = useTheme<Theme>();
        const translate = context?.translate;
        // Lazy load options
        const [, options] = useLazyLoadOptions<ListItem>(componentParams.listItems);
        // Filter them if a filter is provided
        const filteredOptions = useMemo(() => {
            const ops = filterOptions ? filterOptions(options, node, column.getColId()) : options;
            if (!ops.some((item) => currentlySelectedValues?.some((i) => item.id === i.id)) && currentlySelectedValues !== undefined) {
                // Attach the current value to the options if it is not in the options array. This makes the value visible even if we don't have all options yet
                return [...ops, ...currentlySelectedValues];
            }
            return ops;
        }, [options, filterOptions]);

        const [value, setValue] = useState<ListItem[]>(currentlySelectedValues || []);

        const initialValue = useMemo(() => filteredOptions.filter((item) => currentlySelectedValues?.some(({ id }) => id === item.id)), [filteredOptions]);

        // The initial value may change if the options are lazy-loaded
        useEffect(() => setValue(initialValue), [initialValue]);

        const isInvalid = value?.length === 0;

        useImperativeHandle(ref, () => ({
            getValue() {
                return value || initialValue;
            },
            isCancelBeforeStart() {
                // If the field is not required or has a value, while we are pending creation, make this cell uneditable
                const blockEditsUntilCreatedOnServer = (!required || currentlySelectedValues?.length > 0) && node.data.networkStatus === RowNetworkStatus.PendingCreation;
                return !node.data.canEdit || blockEditsUntilCreatedOnServer;
            },
            isCancelAfterEnd() {
                return isInvalid || JSON.stringify(value) === JSON.stringify(currentlySelectedValues);
            },
            isPopup() {
                return true;
            },
            getPopupPosition() {
                return "over";
            },
        }));

        return (
            <Popover
                open
                anchorEl={eGridCell}
                onClose={() => api.stopEditing()}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <StyledContainer>
                    <ConciseSelector
                        multi
                        renderLabel={() => renderMultiSelectLabel(value, { translate })}
                        options={filteredOptions.map(({ id, name }) => ({ id, label: name })).sort((a, b) => a.label.localeCompare(b.label))}
                        values={value?.map((selected) => selected.id) || []}
                        onChange={(selection) => {
                            if (selection.includes(allOptionsValueLiteral)) {
                                setValue(filteredOptions.length === value.length ? [] : filteredOptions);
                            } else {
                                setValue(filteredOptions.filter((option) => selection.includes(option.id)));
                            }
                        }}
                        toggleAllOptions
                        toggleAllOptionsLabel={translate?.("Select All") || "Select All"}
                        formControlStyle={{
                            backgroundColor: theme.dataTableTheme.background,
                            width: column.getActualWidth(),
                            border: theme.dataTableTheme.root.border,
                            borderRadius: theme.dataTableTheme.popover.borderRadius,
                        }}
                    />
                </StyledContainer>
            </Popover>
        );
    }
);

const StyledContainer = styled.div`
    ${cellEditorBase}
`;
